// frontend/src/components/homepage/TickerLive.js
//
// The strip of recent matches that runs under the hero: profession, city, AI
// score, how long ago. It is the page saying "this is happening right now"
// without asking anyone to believe a number in isolation - eight concrete
// matches drifting past is a different claim from "500,000+".
//
// IT IS PURE CSS. There is no rAF task, no timer and no state: the track is
// rendered once and one static keyframe (`hl-marquee`, homepage.css) moves it.
// A marquee is the one effect where the compositor genuinely does the job better
// than we can, and a JS transform per frame would buy nothing except a task to
// forget to cancel.
//
// THE TRACK IS THE LIST TWICE. `hl-marquee` travels exactly `translateX(50%)`,
// i.e. one half of the track, so the last frame is pixel-identical to the first
// and the restart is invisible. Duplication and travel distance are a PAIR -
// changing one without the other puts a visible seam in the loop once per cycle.
//
// aria-hidden, deliberately. Every string here already appears on the page in a
// form that carries meaning (the hero card, and the live feed further down);
// read out a second time as a 32-item list it is noise, and it repeats. The text
// is still real DOM text, so the prerender snapshot and any crawler counting
// characters see it - it is only removed from the accessibility tree.

import React, { useMemo } from "react";
import { LEADS, HOT_SCORE } from "./heroData";
import useMotion from "../../motion/useMotion";
import useInViewGate from "../../motion/useInViewGate";
import { LIVE_ROOT_MARGIN } from "./SectionShell";

/**
 * How many times the lead list appears in ONE half of the track.
 *
 * Two, from the prototype: eight items is not enough to fill a 1440px strip, so
 * a single copy would leave a gap of empty track drifting through the mask.
 */
export const REPEATS_PER_HALF = 2;

/**
 * Build the items for one half of the track.
 *
 * Exported so a test can assert the halves are identical without re-deriving
 * the rule it is checking.
 *
 * @param {object[]} leads
 * @returns {object[]}
 */
export function buildTickerHalf(leads) {
  const half = [];
  for (let copy = 0; copy < REPEATS_PER_HALF; copy += 1) half.push(...leads);
  return half;
}

/** One item: a dot, the profession, the city, the score and the age. */
const TickerItem = ({ lead }) => (
  <span className={lead.score >= HOT_SCORE ? "hl-ticker-item hl-hot" : "hl-ticker-item"}>
    <span className="hl-ticker-pro">{lead.pro}</span>
    <span>{lead.city}</span>
    {/* one string: adjacent text nodes cannot be hydrated from the snapshot */}
    <span className="hl-ticker-score">{`${lead.score}%`}</span>
    <span>{lead.time}</span>
  </span>
);

/**
 * @param {object} props
 * @param {object[]} [props.leads] the deck to scroll. Defaults to the
 *   homepage's own, so `Homepage.js` passes nothing and renders what it always
 *   did; a profession page passes its deck rather than scrolling a mixed strip
 *   under its own hero.
 */
export default function TickerLive({ leads = LEADS }) {
  const { off: motionOff } = useMotion();
  // Not a SectionShell, so it publishes its own `data-live` (the same gate,
  // the same margin): the marquee is paused while the strip is off screen.
  const [gateRef, live] = useInViewGate({ rootMargin: LIVE_ROOT_MARGIN, initialInView: true });

  // The half is stable for the life of the page; only the class on the track
  // changes when a visitor flips the toggle.
  const half = useMemo(() => buildTickerHalf(leads), [leads]);

  return (
    <div
      className="hl-ticker"
      aria-hidden="true"
      data-testid="hl-ticker"
      ref={gateRef}
      data-live={live ? "true" : "false"}
    >
      <div
        className={motionOff ? "hl-ticker-track hl-ticker-static" : "hl-ticker-track"}
        data-animated={motionOff ? "false" : "true"}
      >
        {/* Two halves, rendered from ONE array so they cannot drift apart. The
            key carries the half index because the same lead legitimately appears
            four times in the track. */}
        {[0, 1].map((halfIndex) =>
          half.map((lead, index) => (
            <TickerItem key={`${halfIndex}:${index}:${lead.pro}`} lead={lead} />
          ))
        )}
      </div>
    </div>
  );
}
